import {
  ChartBarIcon,
  ClockIcon,
  DatabaseIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';

const stats = [
  {
    value: '>96%',
    label: 'Accuracy',
    description: 'State-of-the-art speech recognition accuracy',
    icon: ChartBarIcon,
    color: 'text-green-600 dark:text-green-400',
    bg: 'bg-green-100 dark:bg-green-900/30',
  },
  {
    value: '23.2s',
    label: 'Latency on 30 min audio file',
    description: 'Ultra-fast processing speed',
    icon: ClockIcon,
    color: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-100 dark:bg-blue-900/30',
  },
  {
    value: '12.5M',
    label: 'Hours of multilingual training data',
    description: 'Trained on diverse multilingual datasets',
    icon: DatabaseIcon,
    color: 'text-purple-600 dark:text-purple-400',
    bg: 'bg-purple-100 dark:bg-purple-900/30',
  },
];

export default function TranscriptionStats() {
  const { t } = useLocale('ai-transcription');

  return (
    <div className='relative isolate overflow-hidden bg-gray-50 py-24 dark:bg-slate-900'>
      {/* Background elements */}
      <div className='absolute inset-0 -z-10 bg-[radial-gradient(#80808033_1px,transparent_1px)] bg-[size:20px_20px] [mask-image:radial-gradient(ellipse_50%_50%_at_50%_50%,#000_10%,transparent_100%)] opacity-30'></div>

      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='mx-auto max-w-2xl text-center'>
          <div className='mb-4 flex justify-center'>
            <div className='relative inline-flex items-center space-x-2 rounded-full bg-primary-50 px-3 py-1 text-sm font-semibold text-primary-600 ring-1 ring-inset ring-primary-600/20 dark:bg-primary-500/10 dark:text-primary-400 dark:ring-primary-500/20'>
              {t('Performance metrics')}
            </div>
          </div>
          <h2 className='text-3xl font-extrabold tracking-tight text-gray-900 dark:text-slate-200 sm:text-5xl'>
            {t('Industry-Leading Performance')}
          </h2>
          <p className='mt-6 text-lg leading-8 text-gray-600 dark:text-slate-400'>
            {t(
              'Our AI transcription technology delivers exceptional accuracy and speed, powered by advanced machine learning models.',
            )}
          </p>
        </div>

        <div className='mx-auto mt-16 max-w-5xl'>
          <div className='grid grid-cols-1 gap-8 lg:grid-cols-3'>
            {stats.map((stat) => (
              <div
                key={stat.label}
                className='group relative flex flex-col items-center rounded-3xl bg-white/70 p-10 text-center shadow-lg ring-1 ring-gray-900/5 backdrop-blur-md dark:bg-slate-800/60 dark:ring-white/10 hover:-translate-y-2 hover:shadow-2xl hover:ring-primary-500/30 transition-all duration-500 overflow-hidden'
              >
                <div className='absolute inset-0 bg-gradient-to-br from-primary-50/50 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100 dark:from-primary-900/20' />
                <div
                  className={`relative mb-8 flex h-20 w-20 items-center justify-center rounded-2xl group-hover:scale-110 shadow-sm ring-1 ring-inset ring-black/5 transition-transform duration-500 ${stat.bg}`}
                >
                  <stat.icon
                    className={`h-10 w-10 ${stat.color} drop-shadow-md`}
                    aria-hidden='true'
                  />
                </div>
                <div
                  className={`relative text-4xl font-extrabold ${stat.color} lg:text-5xl drop-shadow-sm`}
                >
                  {stat.value}
                </div>
                <h3 className='relative mt-4 text-lg font-bold text-gray-900 dark:text-slate-100'>
                  {t(stat.label)}
                </h3>
                <p className='relative mt-2 text-sm leading-relaxed text-gray-600 dark:text-slate-400'>
                  {t(stat.description)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
